// Importaciones necesarias
import * as THREE from 'three';
import * as CANNON from 'cannon-es';
import { Client } from 'colyseus.js';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import GUI from 'lil-gui';
import { RGBELoader } from 'three/examples/jsm/loaders/RGBELoader.js';

import Ball from './Ball.js';
import Ground from './Ground.js';
import Lights from './Lights.js';

const BACKEND_URL = 'wss://metaforge-websocket-backend.onrender.com';

class Game {
    constructor() {
        this.isLoading = true;
        this.gameState = {
            playerId: null,
            players: {},
            balls: {},
            grounds: [],
            lights: [],
            clock: new THREE.Clock(),
        };

        this.keysPressed = {};

        this.SCENE_BACKGROUND_COLOR = 0xFFFFFF;

        this.sizes = {
            width: window.innerWidth,
            height: window.innerHeight
        };

        this.controlsEnabled = true;

        this.room = null;

        this.remoteBalls = {};

        this.initScene();
        this.initCamera();
        this.initPhysics();
        this.initRenderer();
        this.initControls();

        this.preload().then(() => {
            console.log('Preloaded');
            this.initObjects();
            this.initLights();
            this.initEvents();
            this.initColyseus();

            this.clock = new THREE.Clock();
            this.tick();

            this.isLoading = false;
        });        
    }

    initColyseus() {
        this.client = new Client(BACKEND_URL);

        this.client.joinOrCreate('my_room').then(room => {
            this.room = room;
            console.log("Conectado a la sala:", room.name);
    
            this.gameState.playerId = room.sessionId;
    
            this.setUpStateListeners();
    
            this.room.onMessage('playerMoved', (message) => {
                if (message.sessionId !== this.room.sessionId) {
                    if (this.remoteBalls[message.sessionId]) {
                        this.remoteBalls[message.sessionId].setPosition(
                            message.x,
                            message.y,
                            message.z,
                            new THREE.Vector3(message.velocity.x, message.velocity.y, message.velocity.z)
                        );
                    }
                }
            });
    
            this.room.onMessage('playerJoined', (message) => {
                console.log(`New player joined: ${message.sessionId}`);
                if (message.sessionId !== this.room.sessionId) {
                    const startPosition = new THREE.Vector3(message.x || 0, message.y || 3, message.z || 0);
                    const remoteBall = new Ball(this.scene, this.world, startPosition);
                    remoteBall.isRemote = true;
                    remoteBall.build();
                    this.remoteBalls[message.sessionId] = remoteBall;
                }
            });
    
            this.room.onMessage('playerLeft', (message) => {
                console.log(`Player left: ${message.sessionId}`);
                if (this.remoteBalls[message.sessionId]) {
                    this.scene.remove(this.remoteBalls[message.sessionId].ballMesh);
                    this.world.removeBody(this.remoteBalls[message.sessionId].ballBody);
                    delete this.remoteBalls[message.sessionId];
                }
            });
    
        }).catch(err => {
            console.error("No se pudo unir a la sala:", err);
        });
    }
    

    setUpStateListeners() {
        this.room.state.players.onAdd = (player, sessionId) => {
            console.log(`Jugador añadido: ${sessionId}`);
            if (sessionId === this.room.sessionId) {
                return;
            }

            const startPosition = new THREE.Vector3(player.x, player.y, player.z);
            const remoteBall = new Ball(this.scene, this.world, startPosition);
            remoteBall.isRemote = true;
            remoteBall.build();
            this.remoteBalls[sessionId] = remoteBall;

            player.onChange = (changes) => {
                if (sessionId !== this.room.sessionId) {
                    remoteBall.setPosition(
                        player.x,
                        player.y,
                        player.z,
                        new THREE.Vector3(player.vx, player.vy, player.vz)
                    );
                }
            };
        };

        this.room.state.players.onRemove = (player, sessionId) => {
            console.log(`Jugador eliminado: ${sessionId}`);
            if (this.remoteBalls[sessionId]) {
                this.scene.remove(this.remoteBalls[sessionId].ballMesh);
                this.world.removeBody(this.remoteBalls[sessionId].ballBody);
                delete this.remoteBalls[sessionId];
            }
        };
    }

    initScene() {
        this.scene = new THREE.Scene();
    }

    async loadEnvironmentMap() {
        return new Promise((resolve, reject) => {
            new RGBELoader().load('farm_sunset_1k.hdr', (texture) => {
                texture.mapping = THREE.EquirectangularReflectionMapping;
                this.scene.background = texture;
                this.scene.environment = texture;
                resolve();
            }, undefined, reject);
        });
    }

    async initGround() {
        this.ground = new Ground(this.scene, this.world, {
            dimension: {
                width: 50,
                height: 50
            },
            castShadow: true,
            receiveShadow: true,
            material: {
                color: 0x444444
            },
            physics: {
                materialName: 'groundMaterial',
                mass: 0,
                restitution: -1,
                friction: 0
            }
        });
        await this.ground.build();
    }

    async preload() {
        return Promise.all([
            this.loadEnvironmentMap(),
            this.initGround()
        ]);
    }

    initCamera() {
        this.camera = new THREE.PerspectiveCamera(75, this.sizes.width / this.sizes.height);
        this.camera.position.set(5, 10, 20);
        this.scene.add(this.camera);
    }

    initRenderer() {
        this.canvas = document.querySelector('canvas.webgl');
        this.renderer = new THREE.WebGLRenderer({
            canvas: this.canvas
        });
        this.renderer.setSize(this.sizes.width, this.sizes.height);
        this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
        this.renderer.shadowMap.enabled = true;
        this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    }

    initControls() {
        this.controls = new OrbitControls(this.camera, this.canvas);
        this.controls.enabled = this.controlsEnabled;
        this.controls.enableDamping = true;
    }

    initPhysics() {
        this.world = new CANNON.World();
        this.world.gravity.set(0, -9.82*2, 0);
    }

    initObjects() {
        const ballStartPosition = new THREE.Vector3(0, 3, 0);
        this.ball = new Ball(this.scene, this.world, ballStartPosition);
        this.ball.build();
    }

    initLights() {
        this.lights = new Lights(this.scene, {
            ambientLightColor: 0xffffff,
            ambientLightIntensity: 0.5,
            directionalLightColor: 0xffffff,
            directionalLightIntensity: 0.5,
            directionalLightPosition: new THREE.Vector3(15, 25, 25),
            directionalLightShadowMapSize: new THREE.Vector2(2048, 2048),
            directionalLightShadowCameraLeft: -50,
            directionalLightShadowCameraRight: 50,
            directionalLightShadowCameraTop: 50,
            directionalLightShadowCameraBottom: -50,
            directionalLightShadowCameraNear: 1,
            directionalLightShadowCameraFar: 70
        });
        this.lights.build();
    }

    initEvents() {
        window.addEventListener('resize', () => this.onWindowResize());
        window.addEventListener('keydown', (event) => this.onKeyDown(event));
        window.addEventListener('keyup', (event) => this.onKeyUp(event));
    }

    onWindowResize() {
        this.sizes.width = window.innerWidth;
        this.sizes.height = window.innerHeight;

        this.camera.aspect = this.sizes.width / this.sizes.height;
        this.camera.updateProjectionMatrix();

        this.renderer.setSize(this.sizes.width, this.sizes.height);

        this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    }

    onKeyDown(event) {
        this.keysPressed[event.code] = true;
        this.handleKeyInput();
    }

    onKeyUp(event) {
        this.keysPressed[event.code] = false;
        this.handleKeyInput(event.code);
    }

    handleKeyInput() {
        const inputX = (this.keysPressed['KeyD'] || this.keysPressed['ArrowRight']) ? 1 :
                       (this.keysPressed['KeyA'] || this.keysPressed['ArrowLeft']) ? -1 : 0;
        const inputZ = (this.keysPressed['KeyW'] || this.keysPressed['ArrowUp']) ? -1 :
                       (this.keysPressed['KeyS'] || this.keysPressed['ArrowDown']) ? 1 : 0;

        if (!this.ball.state.isJumping && this.keysPressed['Space']) {
            this.ball.jump();
            return;
        }

        if (inputX !== 0 || inputZ !== 0) {
            const impulseMagnitude = 12;
            const forceMagnitude = 100;
    
            const angle = Math.atan2(inputZ, inputX);
            const directionX = Math.cos(angle);
            const directionZ = Math.sin(angle);
    
            if (!this.previousInput || (this.previousInput.x !== inputX || this.previousInput.z !== inputZ)) {
                const impulseX = directionX * impulseMagnitude;
                const impulseZ = directionZ * impulseMagnitude;
                this.ball.applyImpulse(impulseX, 0, impulseZ);
            }

            const forceX = directionX * forceMagnitude;
            const forceZ = directionZ * forceMagnitude;
            this.ball.applyForce(forceX, 0, forceZ);
    
            this.previousInput = { x: inputX, z: inputZ };
        } else {
            this.previousInput = null;
        }
    }

    updateCameraPosition() {
        const ballPosition = this.ball.ballMesh.position;
        this.camera.position.set(ballPosition.x, ballPosition.y + 10, ballPosition.z + 20);
        this.camera.lookAt(ballPosition);
    }

    sendPlayerPosition() {
        if (this.room) {
            const position = this.ball.ballMesh.position;
            const velocity = this.ball.ballBody.velocity;
            this.room.send('move', {
                sessionId: this.room.sessionId,
                x: position.x,
                y: position.y,
                z: position.z,
                velocity: {
                    x: velocity.x,
                    y: velocity.y,
                    z: velocity.z
                }
            });
        }
    }

    tick() {
        requestAnimationFrame(() => this.tick());

        const deltaTime = this.clock.getDelta();

        this.world.step(1 / 60, deltaTime, 3);

        this.controls.update();

        this.ball.update(deltaTime);

        this.sendPlayerPosition();

        for (let sessionId in this.remoteBalls) {
            this.remoteBalls[sessionId].update(deltaTime);
        }

        this.updateCameraPosition();

        this.renderer.render(this.scene, this.camera);
    }
}

new Game();
